import dayjs from "dayjs";
import { generateTradingDaysForStrike } from "./optionPricing";
import {
  getTradingDaysUntilExpiration,
  easternMarketCloseOnCalendarDate,
} from "./tradingDays";

/**
 * Build the options-data structure from a real fetched option chain — each
 * strike is priced with its OWN live IV (skew-aware) instead of one flat IV
 * for the whole book.
 *
 * @param {object} params
 * @param {number} params.underlyingPrice - Live underlying price (from the chain response)
 * @param {string} params.expiration - Expiration date, YYYYMMDD
 * @param {Array<{strike:number, bid:number, ask:number, last:number, iv:number}>} params.chainStrikes
 *   - iv is already a decimal (e.g. 0.837 for 83.7%), as returned by the chain endpoint
 * @param {number} params.riskFreeRate - Risk-free rate (as percentage, e.g. 4.5 for 4.5%)
 * @param {string} params.time - ISO timestamp the chain quote is as-of (from the chain
 *   response). Shown in local time in the UI; converted to Eastern for the
 *   "now" / entry price. Future scenario prices still use 16:00 ET close.
 * @returns {object} - { [expiration]: { expiration_date, trading_days_count, strikes } }
 */
export function generateLiveOptionsData({
  underlyingPrice,
  expiration,
  chainStrikes,
  riskFreeRate = 4.5,
  time = null,
}) {
  const riskFreeRateDecimal = riskFreeRate / 100;
  const expirationDate = easternMarketCloseOnCalendarDate(
    dayjs(expiration, "YYYYMMDD"),
  );
  const customDate = time ? dayjs(time) : dayjs();
  const tradingDays = getTradingDaysUntilExpiration(expirationDate, customDate);

  const strikesData = chainStrikes.map((chainStrike) => ({
    strike: chainStrike.strike,
    bid: chainStrike.bid,
    ask: chainStrike.ask,
    last: chainStrike.last,
    iv: chainStrike.iv,
    trading_days: generateTradingDaysForStrike(
      underlyingPrice,
      chainStrike.strike,
      expirationDate,
      chainStrike.iv,
      riskFreeRateDecimal,
      customDate,
    ),
  }));

  return {
    [expiration]: {
      expiration_date: expiration,
      trading_days_count: tradingDays.length,
      strikes: strikesData,
    },
  };
}
